import { SyntheticEvent, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomButton, LoadingSpinner, Tabs } from '@medtronic/pecc-react-component-library-js';
import { Paper, Stack, Typography } from '@mui/material';
import { DatasetSourceApiEnum } from '~/api/types';
import { useGetDatasetFilesQuery, useGetDatasetQuery } from '~/api/v2/hooks';
import BreadcrumbsNavigation from '~/components/BreadcrumbsNavigation';
import { useDatasetFileUploader, usePersistedTabValue } from '~/hooks';
import { ArrowBackIcon, HomeIcon, ImportSupportiveDocumentsIcon } from '~/icons';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { PageTabsKeyEnum } from '~/redux/slices/pageTabsSlice';
import { getPriviligies } from '~/utils';
import DatasetUploadingContext from './components/DatasetFilesUploadingContext';
import { DatasetDetailsTabsEnum, getRedirectTabKey, getSelectedTabIndex, useBuildTabsData } from './datasetDetailsTabs';
import { datasetDetailsTabsContainerStyles, iconButtonStyles } from './styles';

const DatasetDetailsPage = () => {
  const datasetId = +useParams().id!;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useGetDatasetQuery({ datasetId });
  const user = useAppSelector(selectCurrentUser);
  const [tabIndex, setTabIndex] = usePersistedTabValue<DatasetDetailsTabsEnum>(
    PageTabsKeyEnum.DATASET_DETAILS,
    DatasetDetailsTabsEnum.DETAILS,
  );

  const { handleUploadedFiles: handleUploadedDataFiles, uploadingFiles: uploadingDataFiles } = useDatasetFileUploader(
    datasetId,
    tabIndex === DatasetDetailsTabsEnum.DATA_FILES,
  );
  const { handleUploadedFiles: handleUploadedSupportiveDocs, uploadingFiles: uploadingSupportiveDocs } =
    useDatasetFileUploader(datasetId, tabIndex === DatasetDetailsTabsEnum.SUPPORTIVE_DOCS, true);

  const { isFetching: isAllDataFetching } = useGetDatasetFilesQuery({
    datasetId,
    supportiveDocs: false,
  });
  const { isFetching: isAllSupportiveDocumentsFetching } = useGetDatasetFilesQuery({
    datasetId,
    supportiveDocs: true,
  });

  const userPriviligies = getPriviligies(user?.role?.id);
  const isManageAccessAllowed =
    userPriviligies?.manageDatasets.canView ||
    data?.data_owner_name === user?.id ||
    data?.secondary_data_owners?.includes(user?.id || '') ||
    false;

  const tabsDataWithKeys = useBuildTabsData({
    isManageAccessAllowed,
    uploadingDataFiles,
    uploadingSupportiveDocs,
  });

  useEffect(() => {
    if (location.state?.tab === DatasetDetailsTabsEnum.MANAGE_ACCESS) {
      setTabIndex(DatasetDetailsTabsEnum.MANAGE_ACCESS);
    }
  }, [isManageAccessAllowed, location.state, setTabIndex]);

  const handleTabChange = (_e: SyntheticEvent, newIndex: number) => {
    const nextTabKey = tabsDataWithKeys[newIndex]?.key;

    if (nextTabKey !== undefined) {
      setTabIndex(nextTabKey);
    }
  };

  const shouldShowImportDataButton =
    tabIndex !== DatasetDetailsTabsEnum.MANAGE_ACCESS &&
    ![DatasetSourceApiEnum.M2M].some((api) => api === data?.source_api) &&
    [data?.source_docs_upload_allowed, data?.supportive_docs_upload_allowed].some(Boolean);

  return (
    <DatasetUploadingContext.Provider value={{ uploadingDataFiles, uploadingSupportiveDocs }}>
      <Stack spacing={2}>
        <BreadcrumbsNavigation
          navigationConfig={[
            { name: <HomeIcon />, path: '' },
            { name: 'Data catalog', path: `..` },
            { name: data?.name, path: `.` },
          ]}
        />
        <CustomButton sx={iconButtonStyles} onClick={() => navigate('..', { relative: 'path' })} buttonType="outlined">
          <ArrowBackIcon />
          Back to datasets
        </CustomButton>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h3">{data?.name}</Typography>
          {shouldShowImportDataButton && (
            <CustomButton
              onClick={() => {
                dispatch(
                  openModal({
                    type: ModalTypesEnum.IMPORT_DATA_TO_DATASET,
                    payload: {
                      onFilesUpload: (supportiveDocuments: File[] | null, dataFiles: File[] | null) => {
                        const nextTabKey = getRedirectTabKey(!!dataFiles, !!supportiveDocuments, tabIndex!);

                        setTabIndex(nextTabKey || DatasetDetailsTabsEnum.DETAILS);
                        handleUploadedSupportiveDocs(supportiveDocuments);
                        handleUploadedDataFiles(dataFiles);
                      },
                    },
                  }),
                );
              }}
              sx={iconButtonStyles}
              size="large"
            >
              <ImportSupportiveDocumentsIcon />
              Import data
            </CustomButton>
          )}
        </Stack>
        <Paper elevation={2} sx={datasetDetailsTabsContainerStyles}>
          {isAllDataFetching || isAllSupportiveDocumentsFetching ? (
            <Stack alignItems="center" mt="3rem">
              <LoadingSpinner spinnerSize="xs" />
            </Stack>
          ) : (
            <Tabs
              width="100%"
              isLeftAlign
              labelSx={{ textTransform: 'none' }}
              setRedirectTabIndexOnChange={handleTabChange}
              redirectTabIndex={getSelectedTabIndex(tabsDataWithKeys, tabIndex!)}
              tabsData={tabsDataWithKeys.map(({ label, content }) => ({ label, content }))}
            />
          )}
        </Paper>
      </Stack>
    </DatasetUploadingContext.Provider>
  );
};

export default DatasetDetailsPage;
